// Flag types indicate the status of the flag.
// The ordering of this enum is also used to determine the order in which the flags appear in the admin dashboard
export enum FlagType {
  Product = 'Product',
  Configuration = 'Configuration',
  // Beta flags should be removed once the feature is released
  Beta = 'Beta',
  Deprecated = 'Deprecated',
  // Alpha or in development
  Alpha = 'Alpha',
  // Configurations, but lesser used
  Tweaks = 'Tweaks',
}

export const flagTypeDescriptions: Record<FlagType, string> = {
  [FlagType.Product]: 'Full products in FOSSA (Released)',
  [FlagType.Configuration]: 'Configuration options for FOSSA organizations',
  [FlagType.Beta]: 'Stable but officially unreleased features',
  [FlagType.Deprecated]: 'Deprecated features in FOSSA',
  [FlagType.Alpha]: 'In development or unstable features',
  [FlagType.Tweaks]: 'Tweaks to FOSSA features',
};

// All flags added must be added and documented to the `flagDescriptions`
// object below. This must be actively maintained by engineers
export const AMAZON_LINUX_SUPPORT = 'amazonLinuxEnabled';
export const ARCHIVE_AND_LICENSE_UPLOAD = 'archiveAndLicenseUpload';
export const CLI_DEFAULT_TO_FIRST_PARTY_SCANS = 'cliDefaultToFirstPartyScans';
export const CLI_LICENSE_SCAN_REQUIRE_FULL_FILE_UPLOADS = 'cliLicenseScanRequireFullFileUploads';
export const DEDUPE_LOCATORS_IN_PLAINTEXT_REPORTS = 'dedupeLocatorsInPlainTextReports';
export const DEPENDENCY_ELECTION = 'dependencyElection';
export const DEPENDENCY_PATH_GLOBAL_ISSUES_REPORT = 'dependencyPathGlobalIssuesReport'; // This is just for large orgs
export const DISABLE_ORG_ALERT_BANNER = 'disableOrgAlertBanner';
export const EXCLUDES_DEPENDENCY_PATHS_FROM_ISSUES_CSV_EXPORT = 'excludeDependencyPathsFromIssuesCsvExport';
export const FILE_PATH_IN_REPORTS = 'filePathInReports';
export const GROUPED_SLACK_NOTIFICATIONS = 'groupedSlackNotifications';
export const IDE_INTEGRATION = 'ideIntegration';
export const LDAP_AUTHENTICATION = 'ldapAuthentication';
export const QUALITY = 'quality';
export const DISABLE_REPORT_REDIRECT = 'disableReportRedirect';
export const REQUIRE_POLICY_ON_UPLOAD = 'requirePolicyOnUpload';
export const SECURITY = 'security';
export const TEAM_UNIQUE_ID = 'teamUniqueId';
export const VENDORED_DEPENDENCY_DEFAULT_TO_ARCHIVE_UPLOAD = 'vendoredDependencyDefaultToArchiveUpload';
export const VULN_ADMIN = 'vulnAdmin';
// Disable the Great Report UI Refresh of 2024
export const DISABLE_REPORT_UI_REFRESH = 'disableReportUiRefresh';
// Feature flag to invert the granular API tokens change
export const GRANULAR_APITOKENS_INVERTED = 'granularAPITokensInverted';
export const SECURITY_PDF_REPORTS = 'securityPDFReports';
export const SBOM_PORTAL = 'sbomPortal';
export const SBOM_PORTAL_PRIVATE = 'sbomPortalPrivate';
// Turns off the SBOM Originals feature
export const DISABLE_SBOM_ORIGINALS = 'disableSbomOriginals';
/**
 * Turns off the Deps V2 rewrite of the Global Package Bundle Report and uses the old version.
 */
export const DISABLE_ORG_BOM_V2 = 'disableOrgBomV2';
export const DISABLE_SBOM_POLICY = 'disableSbomPolicy';
export const ENABLED_DOWNLOADING_LARGE_PROJECT_LICENSING_REPORTS = 'enableDownloadingLargeProjectLicensingReports';
export const PACKAGE_LABELS = 'packageLabels';

// Disables features related to the FOSSA GitHub App.
export const DISABLE_GITHUB_APP = 'disableGitHubApp';

/* Hidden feature flags. These must be manually added to the database, and are not
 * available to be added via the admin panel. To add into the admin UI, you must
 * add below into `flagDescriptions`
 */
// UI for editing of Raw Vulns
export const RAW_VULN_EDIT = 'rawVulnEdit';
// Include all dependencies and licenses in the live report
export const FULL_LIVE_REPORT = 'fullLiveReport';
// Enables the Package Observability Inventory Feature
export const PACKAGE_INVENTORY = 'packageInventoryV2';
export const PACKAGE_INVENTORY_CACHE = 'packageInventoryCache';
export const DISABLE_PACKAGE_VULN_FILTER_CACHE = 'disablePackageVulnFilterCache';
// We want to selectively add this FF to certain orgs that do not want their users importing projects via the UI.
export const DISABLE_ADD_PROJECT_BUTTON = 'disableAddProjectButton';
export const GLOBAL_LICENSE_CORRECTIONS = 'globalLicenseCorrections';
// Changes "Ignore" to "Resolve" in some FE components. Please use this sparingly.
export const IGNORE_RESOLVE_RENAME = 'ignoreResolveRename';
// PDF Compliance Reports V2
export const DISABLE_PDF_COMPLIANCE_REPORTS_V2 = 'disablePDFComplianceReportsV2';
// Marks whether the issue-snapshot backfill has been finished for this org
export const BACKFILL_ISSUE_SNAPSHOTS_COMPLETE = 'backfillIssueSnapshotsComplete';
// Enable projects and teams columns in global package report
export const GLOBAL_PACKAGE_REPORT_PROJECTS_AND_TEAMS_COLUMNS = 'globalPackageReportProjectsAndTeamsColumns';
// Enables reachability details and filters for vuln issues.
export const ENABLE_REACHABILITY = 'reachability';
// User defined dependencies from the CLI don't require licenses, and can create a lot of noise and high number of issues
// if used in a specific way (binary scannning for example). This flag doesn't flag issues for any deps of fetcher `user`
export const SKIP_UNLICENSED_ISSUES_FOR_USER_DEFINED_DEPENDENCIES = 'skipUnlicensedIssuesForUserDefinedDependencies';
// Enables being able to link issues to custom URLs.
export const MANUAL_TICKET_EXPORT = 'manualTicketExport';
export const IMPACT_ASSESSMENT = 'impactAssessment';
export const DISABLE_TASK_LOGS = 'disableTaskLogs';
export const VULN_RANGES_EXPORT = 'vulnRangesExport';
// Enables using the new issue scanners based on dependenciesV2
export const ISSUE_SCANNERS_V2 = 'issueScannersV2';
// Enables accessing the Fix Plans report
export const FIX_PLAN_REPORT = 'fixPlanReport';
// Hard code a CycloneDX standard + attestation to CycloneDX JSON SBOMs
export const HARD_CODED_CYCLONEDX_ATTESTATION = 'hardCodedCycloneDXAttestation';
// tmp: Enables access to the new billing page
export const NEW_BILLING_PAGE = 'newBillingPage';
// Enables features related to Hybrid Projects (CLI Upload projects w/ GitHub support).
export const HYBRID_PROJECTS = 'hybridProjects';

/**
 * This is the Feature Flag that will be checked when deciding whether or not to restrict security features.
 */
export const FULL_SECURITY = 'fullSecurity';

/**
 * This is the Feature Flag that will be checked when deciding whether or not to restrict quality features.
 */
export const FULL_QUALITY = 'fullQuality';

// Flag names and their descriptions to show in the UI
// NOTE: these are loosely ordered by popularity/time of creation
export const flagDescriptions = {
  [QUALITY]: {
    displayName: 'Quality',
    description:
      'Turns limited quality features on for the organization. For full features, also enable Full Quality. If hidden, that means the subscription tier has it included already.',
    type: FlagType.Product,
  },
  [FULL_QUALITY]: {
    displayName: 'Full Quality',
    description:
      'Turns full quality features on for the organization. Without this, the org cannot customize quality policies.',
    type: FlagType.Product,
  },
  [SECURITY]: {
    displayName: 'Security',
    description: 'Turns limited security features on for the organization. (Legacy Billing)',
    type: FlagType.Product,
  },
  [FULL_SECURITY]: {
    displayName: 'Full Security',
    description: 'Turns full security features on for the organization. (Legacy Billing)',
    type: FlagType.Product,
  },
  [ARCHIVE_AND_LICENSE_UPLOAD]: {
    displayName: 'Archive + CLI License Scanning',
    description: 'Turns the archive upload functionality on, which includes CLI License Scanning.',
    type: FlagType.Product,
  },
  [DEPENDENCY_PATH_GLOBAL_ISSUES_REPORT]: {
    displayName: 'Dependency Path in Global Issues Report',
    description:
      'Adds the Dependency Path column to Global Issues CSV Report. The process is very expensive for non-provided projects.',
    type: FlagType.Tweaks,
  },
  [DISABLE_ORG_ALERT_BANNER]: {
    displayName: 'Disable Org Alert Banner',
    description: 'Disable super user external org warning banner when outside of the fossa org',
    type: FlagType.Configuration,
  },
  [LDAP_AUTHENTICATION]: {
    displayName: 'LDAP Authentication',
    description: 'Allows organizations to sign in to FOSSA via LDAP.',
    type: FlagType.Configuration,
  },
  [FILE_PATH_IN_REPORTS]: {
    displayName: 'File Path in Reports',
    description: 'Adds file path information to the reporting UI.',
    type: FlagType.Tweaks,
  },
  [TEAM_UNIQUE_ID]: {
    displayName: 'Team Unique ID',
    description: 'Adds ability to set a unique ID for a team across your organization.',
    type: FlagType.Deprecated,
  },
  [IDE_INTEGRATION]: {
    displayName: 'IDE Integration',
    description: 'Enable usage of the FOSSA IDE integration.',
    type: FlagType.Deprecated,
  },
  [GROUPED_SLACK_NOTIFICATIONS]: {
    displayName: 'Grouped Slack Notifications',
    description: 'Group issue notifications into a single Slack message',
    type: FlagType.Alpha,
  },
  [VENDORED_DEPENDENCY_DEFAULT_TO_ARCHIVE_UPLOAD]: {
    displayName: 'Default to Archive Uploads',
    description: 'CLI defaults to Archive Uploads instead of CLI-side license scans for vendored dependency builds.',
    type: FlagType.Configuration,
  },
  [CLI_LICENSE_SCAN_REQUIRE_FULL_FILE_UPLOADS]: {
    displayName: 'Full-file uploads for CLI License scans',
    description:
      'CLI-side license scans upload full file contents instead of the match string. This uploads customer code to FOSSA\'s servers.',
    type: FlagType.Tweaks,
  },
  [CLI_DEFAULT_TO_FIRST_PARTY_SCANS]: {
    displayName: 'First-party scans for CLI',
    description: 'Default to license scanning the directory being analyzed by the CLI',
    type: FlagType.Configuration,
  },
  [DEDUPE_LOCATORS_IN_PLAINTEXT_REPORTS]: {
    displayName: 'De-dupe locators in Plaintext Reports',
    description:
      'De-duplicates matching locators from dependencies that are both direct and transitive at the same time and keeps only the direct one in plaintext reports for Projects & Release Groups',
    type: FlagType.Beta,
  },
  [EXCLUDES_DEPENDENCY_PATHS_FROM_ISSUES_CSV_EXPORT]: {
    displayName: 'Exclude Dependency Paths from Issues Csv Export',
    description: `If enabled, dependency paths will not be included when downloading csv from global issues page.`,
    type: FlagType.Beta,
  },
  [REQUIRE_POLICY_ON_UPLOAD]: {
    displayName: 'Require Policy on Upload',
    description: 'Reject new projects uploaded from the CLI without an explicitly set policy.',
    type: FlagType.Configuration,
  },
  [DISABLE_ADD_PROJECT_BUTTON]: {
    displayName: 'Disable Add Project Button',
    description: 'Disables the Add Project button on the projects dashboard.',
    type: FlagType.Configuration,
  },
  [DISABLE_PDF_COMPLIANCE_REPORTS_V2]: {
    displayName: 'Disable PDF Compliance Reports V2',
    description: 'Display PDF compliance reports using the old format.',
    type: FlagType.Product,
  },
  [GLOBAL_PACKAGE_REPORT_PROJECTS_AND_TEAMS_COLUMNS]: {
    displayName: 'Global Package Report Projects and Teams Columns',
    description: 'Enable projects and teams columns in the global package report',
    type: FlagType.Beta,
  },
  [DEPENDENCY_ELECTION]: {
    displayName: 'Elect popular revisions for non-provided builds',
    description: 'This reverts a logic change made to simplify DependenciesV2',
    type: FlagType.Deprecated,
  },
  [ENABLE_REACHABILITY]: {
    displayName: 'Enable Reachability',
    description: 'Enables reachability on future analyses',
    type: FlagType.Configuration,
  },
  [DISABLE_REPORT_REDIRECT]: {
    displayName: 'Disable Report Redirect',
    description: 'When off, FOSSA Indirects from ephemerally signed URLs from S3 to signed URLs from FOSSA which redirect to S3. When on, FOSSA directly uses ephemerally signed URLs from S3.',
    type: FlagType.Tweaks,
  },
  [SECURITY_PDF_REPORTS]: {
    displayName: 'Security PDF Reports',
    description:
      'Enables global and project level security PDF reports. Disabled by default in favor of Issue History reports',
    type: FlagType.Deprecated,
  },
  [SBOM_PORTAL]: {
    displayName: 'SBOM Portal',
    description: 'Allows an organization to create and publish to their own SBOM Portal',
    type: FlagType.Alpha,
  },
  [SBOM_PORTAL_PRIVATE]: {
    displayName: 'SBOM Portal Private',
    description: 'Allows an organization to configure a private SBOM Portal',
    type: FlagType.Product,
  },
  [DISABLE_SBOM_ORIGINALS]: {
    displayName: 'Disable SBOM Originals',
    description: 'Disables the SBOM Originals feature',
    type: FlagType.Product,
  },
  [GRANULAR_APITOKENS_INVERTED]: {
    displayName: 'Invert Granular API Tokens',
    description: 'Inverts the granular API tokens feature. This will show API tokens in plain text in the UI',
    type: FlagType.Beta,
  },
  [DISABLE_ORG_BOM_V2]: {
    displayName: 'Disable Org Bom V2',
    description: 'Disables the Deps V2 rewrite of the Global Package Bundle Report and uses the old version.',
    type: FlagType.Configuration,
  },
  [IMPACT_ASSESSMENT]: {
    displayName: 'Impact Assessment',
    description: 'When enabled, partial and complete fix will be displayed alongside security issues.',
    type: FlagType.Alpha,
  },
  [VULN_RANGES_EXPORT]: {
    displayName: 'Vuln Ranges Export',
    description: 'Enables exporting of vulnerability version ranges to a CSV file.',
    type: FlagType.Alpha,
  },
  [DISABLE_REPORT_UI_REFRESH]: {
    displayName: 'Disable Report UI Refresh',
    description: 'Disables the Report UI Refresh work.',
    type: FlagType.Product,
  },
  [DISABLE_SBOM_POLICY]: {
    displayName: 'Disable SBOM Policy',
    description: 'Disables the SBOM Policy feature',
    type: FlagType.Alpha,
  },
  [ENABLED_DOWNLOADING_LARGE_PROJECT_LICENSING_REPORTS]: {
    displayName: 'Enable Downloading Large Project Licensing Reports',
    description: 'Enables directly downloading large project licensing reports (which can be slow and potentially broken).',
    type: FlagType.Deprecated,
  },
  [FIX_PLAN_REPORT]: {
    displayName: 'Fix Plan PDF Report',
    description: 'Enables the "Fix Plan" PDF report for security issues.',
    type: FlagType.Alpha,
  },
  [HARD_CODED_CYCLONEDX_ATTESTATION]: {
    displayName: 'SBOM Attestations',
    description: 'DEMO ONLY!! Hard coded attestations for demoing SBOMs',
    type: FlagType.Tweaks,
  },
  [DISABLE_GITHUB_APP]: {
    displayName: 'Disable GitHub App',
    description: 'Disallows users from Quick Importing projects via the "FOSSA Integration" GitHub App.',
    type: FlagType.Deprecated,
  },
  [PACKAGE_LABELS]: {
    displayName: 'Package Labels',
    description: 'Allow labels to be assigned to packages',
    type: FlagType.Alpha,
  },
} as const;
